import React, { useState } from "react";
import { api, errorMessage } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { AlertTriangle, Trash2 } from "lucide-react";
import { toast } from "sonner";

export const PurgeTestDataCard = ({ onDone }) => {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const doPurge = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/maintenance/purge-test-data", { confirm: "PURGE" });
      const d = data.deleted || {};
      toast.success(`Test data cleared — ${d.users || 0} accounts, ${d.leads || 0} leads, ${d.applications || 0} applications removed.`);
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-rose-200 bg-rose-50/40 p-5" data-testid="purge-test-data-card">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="rounded-sm border border-rose-200 bg-rose-50 p-2 text-rose-600"><AlertTriangle className="h-4 w-4" /></div>
          <div>
            <div className="text-sm font-semibold text-slate-900">Clear demo / test data</div>
            <div className="mt-1 max-w-xl text-xs text-slate-500">
              Removes sample/demo data (fake leads, sample applications, test broker &amp; borrower accounts).
              Your real users and their data are kept.
            </div>
          </div>
        </div>
        <Button onClick={() => { setConfirm(""); setOpen(true); }} variant="outline" className="shrink-0 border-rose-300 bg-white text-rose-600 hover:bg-rose-50" data-testid="open-purge-dialog-btn">
          <Trash2 className="mr-2 h-4 w-4" /> Clear test data
        </Button>
      </div>
      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent data-testid="purge-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Clear demo / test data?</DialogTitle>
            <DialogDescription>
              This permanently deletes sample leads, applications, and test accounts. Real users are preserved. Type <b>PURGE</b> to confirm.
            </DialogDescription>
          </DialogHeader>
          <Input value={confirm} onChange={(e) => setConfirm(e.target.value.toUpperCase())} placeholder="Type PURGE" className="font-mono tracking-widest" data-testid="purge-confirm-input" />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy} data-testid="purge-cancel-btn">Cancel</Button>
            <Button onClick={doPurge} disabled={busy || confirm !== "PURGE"} className="bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50" data-testid="purge-confirm-btn">
              {busy ? "Clearing…" : "Clear test data"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
